import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Quote, Star } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

const googleReviews = [
  {
    author: 'Puneet Agrawal',
    role: 'Head - Sales, Wonder ACP',
    review:
      'Working with Grawish has been a game-changer for Wonder ACP. Their PPC campaigns have consistently delivered high-quality leads and improved our brand visibility. The team’s strategic approach and quick turnaround times truly set them apart. Highly recommended!',
  },
  {
    author: 'Bhupesh Mehta',
    role: 'Senior Vice President, Jamna Auto',
    review:
      "Grawish's dedication and commitment to excellence are exceptional. Whether it's handling urgent projects or maintaining long-term brand consistency, their passion reflects in every delivery. They're not just a service provider; they're a true growth partner for Jamna Auto.",
  },
  {
    author: 'Ashish Tripathi',
    role: 'Managing Director, Dignitas',
    review:
      'The creativity and precision Grawish brings to graphic designing and video production are outstanding. Their designs elevate the brand image, and their videos tell stories that connect emotionally with our audience. It’s been a pleasure collaborating with such a passionate team.',
  },
  {
    author: 'Amit Makhija',
    role: 'Head - Marketing, LUMAX',
    review:
      'Grawish has consistently exceeded our expectations in digital marketing execution. From stunning creatives to high-performing PPC campaigns, they blend creativity with performance beautifully. Their understanding of brand language and market dynamics is simply unmatched.',
  },
];

const TestimonialsSection = () => {
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const updateCurrentSlide = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    updateCurrentSlide();
    carouselApi.on('select', updateCurrentSlide);
    carouselApi.on('reInit', updateCurrentSlide);

    return () => {
      carouselApi.off('select', updateCurrentSlide);
      carouselApi.off('reInit', updateCurrentSlide);
    };
  }, [carouselApi]);

  return (
    <section className="relative py-24 section-light overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle, rgba(147, 51, 234, 0.08) 0%, transparent 68%)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block text-sm uppercase tracking-wider font-bold text-purple-700 bg-purple-100 px-4 py-2 rounded-full mb-4">
            Google Reviews
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Real Feedback From Brands That <span className="gradient-text">Grow With Grawish</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Trusted by growing brands for creative execution, campaign performance, and long-term partnership value.
          </p>
        </motion.div>

        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-full border border-purple-100 shadow-sm">
            <div className="flex items-center gap-1 text-amber-400">
              {Array.from({ length: 5 }).map((_, index) => (
                <Star key={index} className="w-4 h-4 fill-current" />
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-800">4.9/5 average rating</span>
          </div>

          <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-full border border-purple-100 shadow-sm">
            <span className="text-sm font-semibold text-gray-800">450+ reviews</span>
          </div>

          <a
            href="https://share.google/qOoQ4UNUU2oXTmOa6"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full border border-purple-100 shadow-sm text-sm font-semibold text-purple-700 hover:text-purple-800 transition-colors"
          >
            View Google Reviews
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <Carousel
            setApi={setCarouselApi}
            opts={{ align: 'start', loop: googleReviews.length > 1 }}
            className="mx-auto max-w-6xl px-1 sm:px-10"
          >
            <CarouselContent className="-ml-0 sm:-ml-4">
              {googleReviews.map((item, index) => (
                <CarouselItem
                  key={item.author}
                  className="pl-0 sm:pl-4 md:basis-1/2"
                  data-testid={`testimonial-card-${index}`}
                >
                  <article className="card-elevated p-8 h-full min-h-[320px] flex flex-col">
                    <div className="flex items-center justify-between mb-6">
                      <div className="inline-flex items-center gap-1 text-amber-400">
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                          <Star key={starIndex} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                      <div className="w-11 h-11 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
                        <Quote className="w-5 h-5" />
                      </div>
                    </div>

                    <p className="text-gray-600 leading-relaxed text-base mb-6 flex-1">
                      "{item.review}"
                    </p>

                    <div className="pt-5 border-t border-purple-100">
                      <h3 className="font-heading text-lg font-bold text-gray-900">{item.author}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.role}</p>
                    </div>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious className="hidden sm:flex left-0 border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-40" />
            <CarouselNext className="hidden sm:flex right-0 border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-40" />
          </Carousel>

          <div className="flex justify-center gap-2 mt-8" aria-label="Testimonial slider pagination">
            {googleReviews.map((item, index) => (
              <button
                key={item.author}
                type="button"
                onClick={() => carouselApi?.scrollTo(index)}
                className={`h-2.5 rounded-full transition-all ${
                  currentSlide === index ? 'w-8 bg-purple-600' : 'w-2.5 bg-purple-200 hover:bg-purple-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
                aria-pressed={currentSlide === index}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
